import { toBase58 } from "./base58"

// Just enough of the Solana wire format to let a wallet that signs raw bytes
// (the provisioned embedded wallet) sign a server-built transaction: a
// serialized transaction is a compact-u16 count of 64-byte signature slots,
// the slots, then the message bytes the signatures are over. Signing means
// signing the message bytes and writing the signature into this wallet's slot.

function compactU16(bytes, offset) {
  let value = 0
  let shift = 0
  let position = offset
  for (;;) {
    const byte = bytes[position]
    position += 1
    value |= (byte & 0x7f) << shift
    if ((byte & 0x80) === 0) break
    shift += 7
  }
  return [ value, position ]
}

// The message bytes — what the wallet actually signs.
export function transactionMessage(transactionBytes) {
  const [ signatureCount, signaturesStart ] = compactU16(transactionBytes, 0)
  return transactionBytes.slice(signaturesStart + signatureCount * 64)
}

// This wallet's signature slot: its position among the message's required
// signers (the first accounts of the message, fee payer first). -1 when the
// address isn't a signer at all — a transaction we must never sign.
export function signerIndex(messageBytes, address) {
  let offset = 0
  if (messageBytes[offset] & 0x80) offset += 1 // versioned-message prefix
  const requiredSigners = messageBytes[offset]
  offset += 3 // header: three u8 counts
  const [ keyCount, keysStart ] = compactU16(messageBytes, offset)
  for (let index = 0; index < Math.min(requiredSigners, keyCount); index++) {
    const key = messageBytes.slice(keysStart + index * 32, keysStart + (index + 1) * 32)
    if (toBase58(key) === address) return index
  }
  return -1
}

// The signature in slot 0 of a signed transaction: the fee payer's, which is
// the one the cluster indexes the transaction under.
export function firstSignature(transactionBytes) {
  const [ , signaturesStart ] = compactU16(transactionBytes, 0)
  return transactionBytes.slice(signaturesStart, signaturesStart + 64)
}

// A copy of the transaction with `signature` written into slot `index`.
export function withSignature(transactionBytes, index, signature) {
  const [ , signaturesStart ] = compactU16(transactionBytes, 0)
  const signed = transactionBytes.slice()
  signed.set(signature, signaturesStart + index * 64)
  return signed
}

// Hand the signed transaction to the cluster the way an extension wallet
// would, via plain JSON-RPC — no SDK needed for one call.
export async function sendRawTransaction(rpcUrl, transactionBase64) {
  const response = await fetch(rpcUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      jsonrpc: "2.0", id: 1, method: "sendTransaction",
      params: [ transactionBase64, { encoding: "base64", preflightCommitment: "confirmed" } ]
    })
  })
  const payload = await response.json()
  if (payload.error) throw new Error(payload.error.message || "sendTransaction failed")
  return payload.result
}

// Wait until the cluster has confirmed `signature`, so the server's own
// read of the chain, which happens right after, finds it. A transaction
// handed over by the page is only broadcast at that point; the server
// refuses to record a signature it cannot see (2026-09-13, the close
// landed at :53 and the confirm asked at :50).
export async function waitForSignature(rpcUrl, signature, { timeoutMs = 60_000, everyMs = 1_500 } = {}) {
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    const response = await fetch(rpcUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        jsonrpc: "2.0", id: 1, method: "getSignatureStatuses", params: [ [ signature ] ]
      })
    })
    const payload = await response.json().catch(() => ({}))
    const status = payload.result?.value?.[0]
    if (status?.err) throw new Error(`transaction failed on chain: ${JSON.stringify(status.err)}`)
    if (status?.confirmationStatus === "confirmed" || status?.confirmationStatus === "finalized") return status
    await new Promise((resolve) => setTimeout(resolve, everyMs))
  }
  throw new Error("transaction not confirmed in time")
}

export function bytesFromBase64(encoded) {
  return Uint8Array.from(atob(encoded), (character) => character.charCodeAt(0))
}

export function bytesToBase64(bytes) {
  let binary = ""
  bytes.forEach((byte) => { binary += String.fromCharCode(byte) })
  return btoa(binary)
}
